import * as RechartLib from 'recharts'
import { createElement } from 'react'
import { RechartsSerializedJSXElement } from './dataChart.types'

export const hydrateRechartsSerializedJsxElement = ({
    jsonJsxElement,
    data,
    key,
}: {
    jsonJsxElement: RechartsSerializedJSXElement
    data: Record<string, unknown>[]
    key?: string | number
}): React.ReactNode => {
    if (
        !jsonJsxElement ||
        typeof jsonJsxElement !== 'object' ||
        !jsonJsxElement.type
    ) {
        console.error('Invalid JSON element structure:', jsonJsxElement)
        return null
    }

    /**
     * Find the actual component function/class from rechart
     */
    const { type, props: jsonProps = {} } = jsonJsxElement
    const Component = (RechartLib as Record<string, unknown>)[
        type
    ] as React.ComponentType
    if (!Component) {
        console.error(`Component type "${type}" not found in Recharts.`)
        return null
    }
    /**
     * Massage the props
     * - remove children from props if present
     * - replace the data prop with the actual data if needed
     * - add the key prop to the props if provided
     */
    const elementProps = { ...jsonProps }
    if (elementProps.data === '{data}') {
        elementProps.data = data
    }
    delete elementProps.children
    if (key != null) {
        elementProps.key = key
    }

    /**
     * Accounting for children being top level or nested in props.
     * Then process the children recursively.
     */
    const children: React.ReactNode[] = []
    const childrenJson = jsonProps.children ?? jsonJsxElement.children ?? []

    if (Array.isArray(childrenJson)) {
        childrenJson.forEach((childJson, index) => {
            let key = childJson?.props?.key as string | number
            if (
                // Check if key type is supported
                (typeof key !== 'string' && typeof key !== 'number') ||
                // Of type string, check if empty
                (typeof key === 'string' && !key)
            ) {
                key = index
            }

            const childElement = hydrateRechartsSerializedJsxElement({
                jsonJsxElement: childJson,
                data,
                key,
            })
            if (childElement) {
                children.push(childElement)
            }
        })
    } else if (typeof childrenJson === 'object') {
        const childElement = hydrateRechartsSerializedJsxElement({
            jsonJsxElement: childrenJson,
            data,
        })
        if (childElement) {
            children.push(childElement)
        }
    }

    return createElement(Component, elementProps as any, ...children)
}
